import { Box } from "@mui/material";
import { type DragEvent, type FC } from "react";
import type { UUID } from "../../utils/uuid";
import { PaginationWrapper } from "../generic/components/pagination-wrapper";
import { AssetCard } from "./asset-card";
import { FolderCard } from "./folder-card";
import type { Asset } from "./server/digital-asset-service";
import type { Folder } from "./server/folder-service";
import { getFolderName } from "./utils";

export const AssetGrid: FC<{
  assets: Asset[];
  onClickFolder: (folderId: UUID) => void;
  designer?: string | null;
  onClick?: (asset: Asset) => void;
  folders?: Folder[];
  showFolderNames: boolean;
  showFolders?: boolean;
  onAssetDragStart?: (asset: Asset, event: DragEvent) => void;
  onFolderDragStart?: (folder: Folder, event: DragEvent) => void;
  onFolderDragOver?: (event: DragEvent) => void;
  onFolderDrop?: (folderId: Folder["id"], event: DragEvent) => void;
  allFolders?: Folder[];
}> = ({
  assets,
  onClickFolder,
  designer,
  onClick,
  folders,
  showFolderNames,
  showFolders = true,
  onAssetDragStart,
  onFolderDragStart,
  onFolderDragOver,
  onFolderDrop,
  allFolders,
}) => {
  const data = showFolders && folders ? [...folders, ...assets] : assets;

  const isAsset = (item: unknown): item is Asset =>
    typeof item === "object" && item !== null && "folder_id" in item;

  const isFolder = (item: unknown): item is Folder =>
    typeof item === "object" && item !== null && "parent_folder_id" in item;

  return (
    <PaginationWrapper
      data={data}
      itemsPerPage={24}
      renderPage={(items) => (
        <Box display="grid" gap={2} gridTemplateColumns="repeat(auto-fit, minmax(200px, 305px))">
          {items.map((item) => {
            if (isAsset(item)) {
              return (
                <AssetCard
                  key={item.id}
                  asset={item}
                  data-testid="asset"
                  onClick={() => onClick?.(item)}
                  designer={designer}
                  folderName={showFolderNames ? getFolderName(item.folder_id, allFolders) : null}
                  draggable={Boolean(onAssetDragStart)}
                  onDragStart={
                    onAssetDragStart ? (event) => onAssetDragStart(item, event) : undefined
                  }
                />
              );
            } else if (isFolder(item)) {
              return (
                <FolderCard
                  key={item.id}
                  folder={item}
                  onClick={() => onClickFolder(item.id)}
                  draggable={Boolean(onFolderDragStart)}
                  onDragStart={
                    onFolderDragStart ? (event) => onFolderDragStart(item, event) : undefined
                  }
                  onDragOver={onFolderDragOver}
                  onDrop={onFolderDrop ? (event) => onFolderDrop(item.id, event) : undefined}
                />
              );
            }
          })}
        </Box>
      )}
    />
  );
};
